import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useFamily } from "./useFamily";

export interface ChildWithFamily {
  id: string;
  name: string;
  avatar_emoji: string;
  family_id: string;
  custody_enabled: boolean;
  created_at: string;
}

export interface CoparentEntry {
  child_id: string;
  coparent_user_id: string;
  family_id: string;
}

/**
 * Henter ALLE barn fra ALLE familier brukeren er med i, pluss alle child_coparents.
 * Brukes til flat visning på tvers av familier.
 */
export function useAllChildren() {
  const { memberships, isLoading: famLoading } = useFamily();
  const familyIds = memberships.map((m) => m.family_id);
  const familyIdsKey = familyIds.join(",");
  const queryClient = useQueryClient();

  // (kept for backward compat but no longer primary driver)
  const hasAnySplitFamily = memberships.some(
    (m) => (m.families as any)?.home_mode === "split"
  );

  const { data: children = [], isLoading: childrenLoading } = useQuery({
    queryKey: ["all-children", familyIds.join(",")],
    queryFn: async () => {
      if (familyIds.length === 0) return [];
      const { data, error } = await supabase
        .from("children")
        .select("*")
        .in("family_id", familyIds)
        .order("created_at");
      if (error) throw error;
      return data as ChildWithFamily[];
    },
    enabled: familyIds.length > 0,
  });

  const { data: allCoparents = [], isLoading: coparentsLoading } = useQuery({
    queryKey: ["all-child-coparents", familyIds.join(",")],
    queryFn: async () => {
      if (familyIds.length === 0) return [];
      const { data, error } = await supabase
        .from("child_coparents" as any)
        .select("child_id, coparent_user_id, family_id")
        .in("family_id", familyIds);
      if (error) throw error;
      return data as unknown as CoparentEntry[];
    },
    enabled: familyIds.length > 0,
  });

  // Realtime: listen for children table changes
  useEffect(() => {
    if (familyIds.length === 0) return;
    const channel = supabase
      .channel(`all-children-rt-${familyIdsKey}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "children" }, () => {
        queryClient.invalidateQueries({ queryKey: ["all-children"] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [familyIdsKey, queryClient]);

  // Split features visible only if any child has custody_enabled explicitly
  const hasAnySplitChild = children.some((c) => c.custody_enabled);

  // Children visible in custody/exchange: only those with custody_enabled
  const splitChildIds = new Set(
    children.filter((c) => c.custody_enabled).map((c) => c.id),
  );

  return {
    children,
    allCoparents,
    hasAnySplitChild,
    splitChildIds,
    isLoading: famLoading || childrenLoading || coparentsLoading,
  };
}
